<template>
  <transition name="fade">
    <div v-if="displayedText" class="plr-subtitles portrait-style-2">
      <div ref="visibleFrameRef" class="visible-frame">
        <div
          ref="textChunksRef"
          class="text-chunks"
          :dir="getTextDirection(displayedText)"
          :style="{
            top: `${textChunksTop}px`,
            transition: textChunksTop ? 'top 3.6s linear' : 'none'
          }"
        >
          {{ displayedText }}
        </div>
      </div>
      <div class="plr-font-adj">
        <button
          @click="changeSize('down')"
          :disabled="fontSizeValue <= minFontsize"
          class="down"
        />
        <button
          @click="changeSize('up')"
          :disabled="fontSizeValue >= maxFontSize"
          class="up"
        />
      </div>
    </div>
  </transition>
</template>

<script lang="ts">
import { computed, ref, watchEffect, onActivated, onDeactivated } from "vue";
import { useStore } from "vuex";
import { useCssVar, useTimestamp, useElementSize } from "@vueuse/core";
import _ from "lodash";
import moment from "moment";
import { getTextDirection } from "@/helpers/string";

export default {
  setup() {
    const store = useStore();

    const timestamp = useTimestamp({ interval: 10 });

    const subtitles = computed(() => store.state.session.subtitles),
      fullText = computed(() =>
        _.join(
          _.map(subtitles.value, s => s?.[0] ?? ""),
          ""
        )
      ),
      displayedText = computed(() =>
        _.join(
          _.transform(subtitles.value, (result: string[], s, i: number) => {
            const transition = 50;

            const sliced = (s?.[0] ?? "").slice(
              0,
              _.max([
                0,
                _.floor(
                  (timestamp.value -
                    _.max([
                      i
                        ? subtitles.value[0]?.[1] +
                          _.reduce(result, (sum, v) => sum + v.length, 0) *
                            transition
                        : 0,
                      s?.[1]
                    ])!) /
                    transition
                )
              ])
            );
            result.push(sliced);
            return sliced.length === (s?.[0] ?? "").length;
          }),
          ""
        )
      );

    // Track heights for smooth Y-scrolling
    const textChunksRef = ref<HTMLElement | null>(null),
      visibleFrameRef = ref<HTMLElement | null>(null),
      { height: textChunksHeight } = useElementSize(textChunksRef),
      { height: visibleFrameHeight } = useElementSize(visibleFrameRef),
      textLinesVisibleCssVal = useCssVar("--subtitles-text-lines-visible"),
      emptyLinesVisibleCssVal = useCssVar("--subtitles-empty-lines-visible"),
      textChunksTop = computed(() =>
        subtitles.value?.length < 1
          ? 0
          : _.min([
              0,
              (visibleFrameHeight.value ?? 0) *
                (1 -
                  parseInt(emptyLinesVisibleCssVal.value || "2", 10) /
                    (parseInt(emptyLinesVisibleCssVal.value || "2", 10) +
                      parseInt(textLinesVisibleCssVal.value || "3", 10))) -
                (textChunksHeight.value ?? 0)
            ])
      );

    // Auto-clear subtitles after 5 seconds of silence
    watchEffect(() => {
      const expiresAt = +moment(timestamp.value).subtract(5, "s");

      if (
        subtitles.value?.length &&
        !_.findLast(subtitles.value, s => s?.[1] > expiresAt) &&
        fullText.value === displayedText.value
      ) {
        store.commit("session/UNSET_SUBTITLES");
      }
    });

    const fontsizeCssVal = useCssVar("--subtitles-fontsize"),
      fontsizeCssDefaultVal = fontsizeCssVal.value;

    const fontSizeValue = ref<number>(),
      fontSizeUnit = ref<string>();

    const syncFontSize = () => {
      const match = fontsizeCssVal.value?.match(/^([\d.]+)([a-zA-Z%]+)$/);

      if (match) {
        fontSizeValue.value = +match[1];
        fontSizeUnit.value = match[2];
        return;
      }
    };

    onActivated(() => syncFontSize());
    onDeactivated(() => (fontsizeCssVal.value = fontsizeCssDefaultVal));

    watchEffect(() => {
      if (fontSizeValue.value && fontSizeUnit.value) {
        fontsizeCssVal.value = `${fontSizeValue.value}${fontSizeUnit.value}`;
      }
    });

    const minFontsize = computed(() => (fontSizeUnit.value === "px" ? 8 : 0.3)),
      maxFontSize = computed(() => (fontSizeUnit.value === "px" ? 50 : 3));

    const changeSize = (direction: "up" | "down") => {
      const step = fontSizeUnit.value === "px" ? 2 : 0.1;

      if (direction === "up") {
        fontSizeValue.value = _.min([
          (fontSizeValue.value ?? 0) + step,
          maxFontSize.value
        ]);
      } else if (direction === "down") {
        fontSizeValue.value = _.max([
          (fontSizeValue.value ?? 0) - step,
          minFontsize.value
        ]);
      }
    };

    return {
      displayedText,
      fontSizeValue,
      fontSizeUnit,
      minFontsize,
      maxFontSize,
      changeSize,
      getTextDirection,
      textChunksRef,
      visibleFrameRef,
      textChunksTop,
      ..._.pick(_, ["split", "map", "join"])
    };
  }
};
</script>

<style lang="scss">
@import "../assets/styles/subtitles-component.scss";
@import "../assets/styles/kiosk-landscape-30inch/subtitles-component.scss";
@import "../assets/styles/kiosk-portrait-30inch/subtitles-component.scss";
</style>

<style lang="scss" scoped>
:root {
  /* Japanese Kana - unique to Japanese */
  @font-face {
    font-family: "SubtitlesFont";
    src: local("Noto Sans JP"),
      url("../assets/fonts/Noto-Sans/NotoSansJP-Medium.woff2") format("woff2");
    unicode-range: U+3040-30FF, /* Hiragana + Katakana */ U+31F0-31FF,
      /* Katakana Phonetic Extensions */ U+FF00-FFEF; /* Halfwidth/Fullwidth Forms */
    font-weight: 500;
    font-display: swap; // optional;
  }

  /* Korean Hangul - unique to Korean */
  @font-face {
    font-family: "SubtitlesFont";
    src: local("Noto Sans KR"),
      url("../assets/fonts/Noto-Sans/NotoSansKR-Medium.woff2") format("woff2");
    unicode-range: U+AC00-D7AF, U+1100-11FF, U+A960-A97F; /* Hangul Syllables + Jamo */
    font-weight: 500;
    font-display: swap; // optional;
  }

  /* Hebrew */
  @font-face {
    font-family: "SubtitlesFont";
    src: local("Noto Sans Hebrew"),
      url("../assets/fonts/Noto-Sans/NotoSansHebrew-Medium.woff2")
        format("woff2");
    unicode-range: U+0590-05FF, U+FB1D-FB4F;
    font-weight: 500;
    font-display: swap; // optional;
  }

  /* Arabic */
  @font-face {
    font-family: "SubtitlesFont";
    src: local("Noto Sans Arabic"),
      url("../assets/fonts/Noto-Sans/NotoSansArabic-Medium.woff2")
        format("woff2");
    unicode-range: U+0600-06FF, U+0750-077F, U+08A0-08FF, U+FB50-FDFF;
    font-weight: 500;
    font-display: swap; // optional;
  }

  /* Chinese/Japanese/Korean (CJK) */
  @font-face {
    font-family: "SubtitlesFont";
    src: local("Noto Sans SC"),
      url("../assets/fonts/Noto-Sans/NotoSansSC-Medium.woff2") format("woff2");
    unicode-range: U+4E00-9FFF, U+3400-4DBF; /* CJK Unified Ideographs */
    font-weight: 500;
    font-display: swap; // optional;
  }

  /* Basic Latin and common symbols */
  @font-face {
    font-family: "SubtitlesFont";
    src: local("Noto Sans"),
      url("../assets/fonts/Noto-Sans/NotoSans-Medium.woff2") format("woff2");
    unicode-range: U+0000-00FF, U+0100-024F, U+0300-03FF, U+1E00-1EFF; /* Latin, Greek, Cyrillic */
    font-weight: 500;
    font-display: swap; // optional;
  }
}

.plr-subtitles {
  font-family: "SubtitlesFont", "Noto Sans", "Roboto", "Segoe UI",
    "Helvetica Neue", Arial, sans-serif;
}
</style>
